import { Close, RadioButtonChecked, RadioButtonUnchecked, Search } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";

import Helpers from "../../../commons/helpers";
import { IResource } from "../../../commons/interfaces";
import useStrings from "../../../hooks/useStrings";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import ControlLabelCheckBox from "../../ControlLabelCheckBox";
import FormField from "../../FormField";
import Modal from "../../Modal";
import { ItemType, avatarAndName, itemBox, typography } from "./styles";

interface IProps {
    visibled: boolean;
    serviceCode: string;
    resourceData: IResource[];
    resources: IResource[];
    onClose: () => void;
    onSubmit: (resourceData: IResource[]) => void;
}

export const ModalAddResourceController: React.FC<IProps> = ({ visibled,serviceCode, resourceData, resources: baseResources, onClose, onSubmit }: IProps) => {
    const strings = useStrings();

    const { onError } = useCommonComponentContext();
    const [search, setSearch] = useState<string>("");
    const [checked, setChecked] = useState<boolean>(false);
    const [data, setData] = useState<IResource[]>(resourceData);
    const [loading, setLoading] = useState<boolean>(true);

    const [resources, setResources] = useState<IResource[]>([]);

    useEffect(() => {
        const getAllResource = async () => {
            try {
                setLoading(true);

                setTimeout(() => {
                    setResources(baseResources || []);
                    setLoading(false);
                }, 1000);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        };
        getAllResource();
    }, [baseResources]);

    const onSearch = () => {};

    const handleSelectItem = (item: IResource) => {
        setData((prev) => {
            if (prev.map((el) => el.resourceCode).includes(item.resourceCode)) {
                const temp = [...prev].filter((el) => el.resourceCode !== item.resourceCode);
                return temp;
            }
            return [...prev, item];
        });
    };

    const handleChecked = (value: boolean) => {
        setChecked(value);
    };

    const renderItem = useCallback(
        (item: IResource) => {
            const isSelected = data.findIndex((el) => el.resourceCode === item.resourceCode) !== -1;
            return (
                <Box sx={(theme) => itemBox(theme, { isSelected, type: ItemType.User })} onClick={() => handleSelectItem(item)}>
                    <Box sx={avatarAndName}>
                        <Box>
                            <Typography
                                sx={(theme: Theme) =>
                                    typography(theme, {
                                        fontSize: "0.8rem",
                                        fontWeight: "bold",
                                    })
                                }
                            >
                                {Helpers.getMultiContentLanguage(item.resourceName)}
                            </Typography>
                            <Typography sx={typography}>{item.resourceURI}</Typography>
                        </Box>
                    </Box>
                    <Box>
                        {isSelected ? (
                            <RadioButtonChecked color="info" sx={{ fontSize: "1rem" }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: "1rem" }} />
                        )}
                    </Box>
                </Box>
            );
        },
        [handleSelectItem, resources, data]
    );

    const filteredData = useMemo(() => {
        if (checked && !search) return data;
        if (!checked && !search) return resources;
        if (checked && search)
            return (
                data.filter((item) => Helpers.getMultiContentLanguage(item.resourceName).toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
                []
            );
        if (!checked && search)
            return (
                resources.filter((item) =>
                    Helpers.getMultiContentLanguage(item.resourceName).toLocaleLowerCase().includes(search.toLocaleLowerCase())
                ) || []
            );
    }, [search, resources, checked]);

    const handleSubmit = (resourceData: IResource[]) => {
        onSubmit(resourceData);
        return onClose();
    };

    return (
        <Modal
            fullWidth
            maxWidth="xs"
            // hasActionButton
            // onAction={() => handleAction()}
            onClose={onClose}
            visible={visibled}
            title={strings.Role.RESOURCE}
            buttonAction={strings.Common.SAVE}
        >
            <Box sx={{ height: 404, overflowY: "scroll" }}>
                {loading && (
                    <Box height={1} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress />
                    </Box>
                )}
                {!loading && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-end",
                            }}
                        >
                            <FormField
                                fullWidth
                                size="small"
                                defaultValue={search || ""}
                                placeholder={`${strings.Common.SEARCH}...`}
                                onBlur={(value: any) => setSearch(value)}
                                onKeyPress={(e: any) => {
                                    if (e.key === "Enter") {
                                        setSearch(e.target.value);
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <IconButton color="info" onClick={() => onSearch()}>
                                            <Search />
                                        </IconButton>
                                    ),
                                    endAdornment: search && (
                                        <IconButton
                                            color="secondary"
                                            onClick={() => {
                                                setSearch(undefined);
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    ),
                                }}
                            />
                            <ControlLabelCheckBox value={checked} label={strings.Common.ALL} onChangeValue={(value) => handleChecked(value)} />
                        </Box>
                        <Box
                            sx={{
                                mt: 1,
                                display: "grid",
                                gap: "0.5rem",
                                overflowY: "scroll",
                            }}
                        >
                            {filteredData.map(renderItem)}
                        </Box>
                    </>
                )}
            </Box>
            <Box gap={1} display="flex" justifyContent="flex-end" alignItems="flex-end" my={0.5}>
                <Button color="secondary" onClick={onClose}>
                    {strings.Common.CANCEL}
                </Button>

                <Button onClick={() => handleSubmit(data)}>{strings.Common.SAVE}</Button>
            </Box>
        </Modal>
    );
};

export default ModalAddResourceController;
